'use strict';

angular.module('ClinicApp').directive('clinicProcedurePricing', (OpencareConstants, OCValidationUtil, OCFormUtil) => {
  const PRICE_TYPES = OpencareConstants.clinicProcedurePriceType;

  // The price types that carry a price. Every other type stores a null price.
  const PRICED_PRICE_TYPES = [PRICE_TYPES.STARTS_AT, PRICE_TYPES.FLAT];

  return {
    scope: {
      allProcedures: '=',
      clinicProcedures: '=',
      editMode: '=',
      onStartEdit: '&',
      onSave: '&',
      onCancel: '&',
    },
    templateUrl: '/views/directives/_clinic-procedure-pricing.html',
    link: function($scope) {
      $scope.priceTypes = PRICE_TYPES;

      // Ordered the way they are offered in the dropdown.
      $scope.priceTypeOptions = [{
        value: PRICE_TYPES.NOT_SET,
        label: 'Not set',
      }, {
        value: PRICE_TYPES.STARTS_AT,
        label: 'Starting at',
      }, {
        value: PRICE_TYPES.FLAT,
        label: 'Flat price',
      }, {
        value: PRICE_TYPES.FREE,
        label: 'Free',
      }, {
        value: PRICE_TYPES.NOT_OFFERED,
        label: 'Not offered',
      }];

      const getClinicProcedure = (procedure) =>
        $scope.clinicProcedures.find((clinicProcedure) => clinicProcedure.procedure.id === procedure.id);

      const initialize = () => {
        $scope.isSubmitted = false;
        $scope.isConfigured = $scope.clinicProcedures.length > 0;

        $scope.procedurePrices = $scope.allProcedures.map((procedure) => {
          const clinicProcedure = getClinicProcedure(procedure);

          return {
            procedure,
            priceType: clinicProcedure?.priceType ?? null,
            price: clinicProcedure?.price ?? null,
          };
        });

        const lastUpdatedAt = $scope.clinicProcedures
          .map(({ updatedAt }) => updatedAt)
          .sort()
          .at(-1);
        $scope.lastUpdatedAt = $scope.isConfigured && lastUpdatedAt
          ? moment(lastUpdatedAt).toDate()
          : null;
      };

      $scope.$watch('allProcedures', initialize);
      $scope.$watch('clinicProcedures', initialize);

      $scope.isStoredProcedure = (procedure) => !!getClinicProcedure(procedure);

      /**
       * A price only applies to the price types that carry one.
       */
      $scope.isPriceable = (procedurePrice) => PRICED_PRICE_TYPES.includes(procedurePrice.priceType);

      $scope.onPriceTypeChange = (procedurePrice) => {
        if (!$scope.isPriceable(procedurePrice)) {
          procedurePrice.price = null;
        }
      };

      $scope.isPriceValid = (procedurePrice) => {
        if (!$scope.isPriceable(procedurePrice)) {
          return true;
        }

        const { price } = procedurePrice;

        // The API stores prices in whole dollars.
        return OCValidationUtil.isWholeDollarAmount(price);
      };

      const toProcedureInput = (procedurePrice) => ({
        procedureId: procedurePrice.procedure.id,
        priceType: procedurePrice.priceType,
        price: $scope.isPriceable(procedurePrice) ? procedurePrice.price : null,
      });

      /**
       * Only the rows the clinic actually changed.
       */
      const getModifiedProcedures = () => $scope.procedurePrices.filter((procedurePrice) => {
        // Never given a price type: nothing to send.
        if (!procedurePrice.priceType) {
          return false;
        }

        const clinicProcedure = getClinicProcedure(procedurePrice.procedure);

        if (!clinicProcedure) {
          return true;
        }

        const { priceType, price } = toProcedureInput(procedurePrice);

        return priceType !== clinicProcedure.priceType || price !== (clinicProcedure.price ?? null);
      });

      $scope.hasPriceError = (procedurePrice) =>
        $scope.isSubmitted && !$scope.isPriceValid(procedurePrice);

      $scope.isSaveDisabled = () => !!$scope.isSaving;

      $scope.save = () => OCFormUtil.submit($scope, async () => {
        const modifiedProcedures = getModifiedProcedures();

        // Nothing changed is a no-op rather than an error: close the panel and discard.
        if (!modifiedProcedures.length) {
          $scope.cancel();
          return;
        }

        // A price the API would reject: keep the panel open with the errors shown.
        if (modifiedProcedures.some((procedurePrice) => !$scope.isPriceValid(procedurePrice))) {
          return;
        }

        await $scope.onSave({
          procedureInputs: modifiedProcedures.map(toProcedureInput),
        });
      });

      $scope.cancel = () => {
        initialize();
        return $scope.onCancel();
      };

      $scope.startEdit = () => {
        $scope.isSubmitted = false;
        return $scope.onStartEdit();
      };
    },
  };
});
